import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

// Affiliate codes are: lowercase, alphanumeric + hyphens, 2-40 chars
const VALID_CODE = /^[a-z0-9-]{2,40}$/;

// Reserved codes that should never be treated as affiliate codes
const RESERVED = new Set([
  'admin', 'api', 'app', 'auth', 'dashboard', 'help', 'home',
  'landing', 'login', 'logout', 'pricing', 'privacy', 'r',
  'signin', 'signup', 'support', 'terms', 'm', 's'
]);

const ATTRIBUTION_TTL_DAYS = 90;

export default function AffiliateRedirect() {
  const { code } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const normalized = (code || '').toLowerCase().trim();

    if (VALID_CODE.test(normalized) && !RESERVED.has(normalized)) {
      // Don't overwrite an existing attribution — first-touch wins
      const existing = localStorage.getItem('revfer_ref');
      if (!existing) {
        const attribution = {
          code: normalized,
          captured_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + ATTRIBUTION_TTL_DAYS * 86400000).toISOString()
        };
        try {
          localStorage.setItem('revfer_ref', JSON.stringify(attribution));
          // Belt-and-suspenders: also set a cookie
          const maxAge = ATTRIBUTION_TTL_DAYS * 86400;
          document.cookie = `revfer_ref=${normalized}; Max-Age=${maxAge}; Path=/; SameSite=Lax; Secure`;
        } catch (e) {
          // localStorage disabled — fall back to cookie only
          console.warn('localStorage unavailable for affiliate attribution', e);
        }
      }
    }

    // Always redirect to home, regardless of whether code was valid
    // (silent failure prevents bots from probing valid codes)
    navigate('/', { replace: true });
  }, [code, navigate]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
      <CircularProgress size={28} />
      <Typography variant="body2" color="text.secondary">Loading...</Typography>
    </Box>
  );
}